import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '../api/client'
import { acceptOAuthLogin } from '../api/mcp-oauth'
import BrandLogo from '../components/BrandLogo'
import { useAuth } from '../hooks/useAuth'
import { startMcpGoogleLogin } from '../utils/mcp-login-continuation'

/**
 * Hydra login page for MCP OAuth (issue #112).
 *
 * The browser arrives here from `GET /oauth2/auth` (via Hydra) with
 * `?login_challenge=...`. If the user is not signed in, they sign in first
 * with their normal ContextHarbor credentials; once authenticated the page
 * accepts the challenge for that JWT identity and follows Hydra's
 * `redirect_to` back into the OAuth flow. Denials never mint tokens — there
 * is no deny path here, the user simply does not proceed.
 */
export default function OAuth2LoginPage() {
  const [searchParams] = useSearchParams()
  const challenge = searchParams.get('login_challenge') ?? ''
  const { user, isAuthenticated, isLoading: isAuthLoading, login } = useAuth()
  const [emailOrUsername, setEmailOrUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState('')
  // Auto-accept fires once per challenge (guarded so the accept identity
  // changing on isAccepting transitions can't retrigger it).
  const autoAcceptedRef = useRef<string | null>(null)

  const accept = useCallback(async () => {
    if (!challenge || isAccepting) return
    setIsAccepting(true)
    setError('')
    try {
      const { redirect_to } = await acceptOAuthLogin(challenge)
      window.location.href = redirect_to
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not continue to the app'
      setError(message)
      toast.error(message)
      setIsAccepting(false)
    }
  }, [challenge, isAccepting])

  // Already signed in → accept immediately (one click saved in the client flow).
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && challenge && autoAcceptedRef.current !== challenge) {
      autoAcceptedRef.current = challenge
      void accept()
    }
  }, [isAuthLoading, isAuthenticated, challenge, accept])

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSigningIn(true)
    try {
      await login(emailOrUsername, password)
      toast.success('Signed in — continuing to the app…')
      // `login` updates auth state; the effect above accepts the challenge.
      // If it somehow doesn't fire (e.g. challenge missing), surface that.
      if (!challenge) setError('Signed in, but the login challenge is missing.')
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Sign-in failed'
      setError(message)
      toast.error(message)
      setIsSigningIn(false)
    }
  }

  if (!challenge) {
    return (
      <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4 px-4">
        <BrandLogo size={48} src="/icon-256.png" className="rounded-2xl" />
        <h1 className="text-xl font-bold text-harbor-text">Connect to ContextHarbor</h1>
        <p className="max-w-md text-center text-sm text-harbor-text-secondary">
          This page handles sign-in from your MCP client. It was opened without a login challenge —
          start again from your client (add this server by URL), or{' '}
          <Link to="/login" className="text-emerald-400 underline">
            sign in normally
          </Link>
          .
        </p>
      </div>
    )
  }

  if (isAuthLoading) {
    return (
      <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
        <p className="text-harbor-text-secondary">Loading…</p>
      </div>
    )
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4 px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
        <p className="text-harbor-text-secondary">
          Continuing as {user?.username ?? user?.email ?? 'you'}…
        </p>
        {error && (
          <div className="max-w-md space-y-3 text-center">
            <p className="text-sm text-red-400">{error}</p>
            <button
              type="button"
              onClick={() => void accept()}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen app-sky flex flex-col justify-center px-4 py-10 sm:px-8">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <BrandLogo size={64} src="/icon-256.png" className="mx-auto rounded-2xl mb-4" />
          <h1 className="text-2xl font-bold text-harbor-text">Connect to ContextHarbor</h1>
          <p className="text-harbor-text-secondary mt-2">
            Your MCP client asked to connect. Sign in to continue to consent.
          </p>
        </div>
        <button
          type="button"
          onClick={() => startMcpGoogleLogin(challenge)}
          className="w-full mb-4 py-3 px-4 rounded-lg border border-harbor-border text-harbor-text"
        >
          Continue with Google
        </button>
        <form onSubmit={handleSignIn} className="space-y-4">
          <div>
            <label
              htmlFor="oauth2-email"
              className="block text-sm font-medium text-harbor-text-secondary mb-1"
            >
              Username or Email
            </label>
            <input
              id="oauth2-email"
              name="oauth2-email"
              type="text"
              value={emailOrUsername}
              onChange={(e) => {
                setEmailOrUsername(e.target.value)
                setError('')
              }}
              required
              autoComplete="username"
              className="w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="username or you@example.com"
            />
          </div>
          <div>
            <label
              htmlFor="oauth2-password"
              className="block text-sm font-medium text-harbor-text-secondary mb-1"
            >
              Password
            </label>
            <input
              id="oauth2-password"
              name="oauth2-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="••••••••"
            />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={isSigningIn}
            className="w-full py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSigningIn ? 'Signing in…' : 'Sign in and continue'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-harbor-text-secondary">
          Changed your mind? Close this tab — nothing is granted until you approve consent next.
        </p>
      </div>
    </div>
  )
}
